import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import {
    ShieldCheck,
    Download,
    Mail,
    Calendar,
    AlertTriangle,
    Loader2,
    CheckSquare,
    Square,
} from 'lucide-react';

import { useApi } from '../lib/api.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { PageHeader, ErrorPage } from '../components/ui/Feedback.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Input, Select } from '../components/ui/Form.jsx';
import { PendingUploadsPanel } from './PendingUploadsPanel.jsx';

// =============================================================================
// CompliancePage — owner-only TFL/authority report generation
// =============================================================================
// Lives at "/compliance". Sidebar entry is owner-gated, but we also gate the
// page itself so a non-owner navigating directly via URL hits the wall.
//
// Three report types:
//   1. Weekly chauffeur report — LIVE (this slice)
//   2. Weekly vehicle report — coming next slice
//   3. Bookings report (last 6 months) — coming after that
//
// Layout: stacked sections, one per report type. The "coming soon" sections
// are real placeholders but are explicit about what they'll do — no
// vaporware framing.
// =============================================================================

const AUTHORITIES = ['TFL', 'Castle Point', 'Wolverhampton', 'Other'];

export function CompliancePage() {
    const { isOwner, isLoading } = useCurrentUser();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16 text-ink-subtle text-sm">
                Loading…
            </div>
        );
    }

    if (!isOwner) {
        return (
            <ErrorPage message="Compliance reports are only available to owners. If you need access, please contact the system owner." />
        );
    }

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3">
                        <ShieldCheck className="h-6 w-6 text-ink-subtle" />
                        <span>Compliance reports</span>
                    </span>
                }
                subtitle="Generate licensing-authority reports for audit and submission."
            />

            <div className="space-y-5">
                                <PendingUploadsPanel />
                <ChauffeurWeeklyReport />
                <VehicleWeeklyReport />
                <BookingsReport />
            </div>
        </div>
    );
}

// =============================================================================
// ComingSoonCard — placeholder for vehicle + bookings reports
// =============================================================================
function ComingSoonCard({ title, description }) {
    return (
        <div className="card border-dashed">
            <div className="card-body">
                <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-paper-hover flex items-center justify-center text-ink-faint flex-shrink-0">
                        <ShieldCheck className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-sm font-semibold text-ink">{title}</h2>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-2xs font-medium uppercase tracking-wider bg-paper-hover text-ink-subtle">
                                Coming soon
                            </span>
                        </div>
                        <p className="text-sm text-ink-subtle mt-1.5">{description}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// PendingUploadsSection — Slice 9.1 Chunk B.4
// =============================================================================
// Owner-only review queue for portal document uploads. Sits at the top of
// the compliance page because reviewing pending docs is a recurring task,
// while reports are weekly/monthly. FIFO list of pending docs across all
// chauffeurs and vehicles, with View/Approve/Reject actions per row.
//
// Reject opens a modal collecting a reason from a fixed list; "Other"
// requires free-text notes. Approve fires immediately (no confirm modal —
// the action is reversible if needed by the owner uploading a corrected
// version themselves).
//
// The list collapses to a header when count=0 so an empty queue isn't
// visual noise.
// =============================================================================
function PendingUploadsSection() {
    const api = useApi();
    const queryClient = useQueryClient();

    const pendingQuery = useQuery({
        queryKey: ['compliance', 'pending'],
        queryFn: () => api.get('/compliance/pending'),
        staleTime: 30 * 1000,
    });

    // Modal state — null when closed, otherwise the doc being rejected.
    const [rejectingDoc, setRejectingDoc] = useState(null);

    // Approve mutation — fires per-row, refetches pending on success.
    const approveMutation = useMutation({
        mutationFn: (docId) => api.post(`/documents/${docId}/approve`),
        onSuccess: (_, docId) => {
            toast.success('Document approved');
            queryClient.invalidateQueries({ queryKey: ['compliance', 'pending'] });
        },
        onError: (err) => {
            toast.error(err.message || 'Could not approve document');
        },
    });

    // Reject mutation — fires from the modal with reason + notes.
    const rejectMutation = useMutation({
        mutationFn: ({ docId, reason, notes }) =>
            api.post(`/documents/${docId}/reject`, { reason, notes }),
        onSuccess: () => {
            toast.success('Document rejected');
            queryClient.invalidateQueries({ queryKey: ['compliance', 'pending'] });
            setRejectingDoc(null);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not reject document');
        },
    });

    const pending = pendingQuery.data?.pending || [];
    const count = pending.length;

    return (
        <div className="card">
            <div className="card-header">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-sm font-semibold text-ink">
                            Pending uploads
                            {count > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-2xs font-semibold">
                                    {count}
                                </span>
                            )}
                        </h2>
                        <p className="text-xs text-ink-subtle mt-0.5">
                            Documents uploaded by chauffeurs awaiting your review.
                        </p>
                    </div>
                </div>
            </div>

            {pendingQuery.isLoading && (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">Loading…</p>
                </div>
            )}

            {pendingQuery.isError && (
                <div className="card-body">
                    <div className="flex items-start gap-3 text-sm text-status-danger">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>{pendingQuery.error?.message || 'Could not load pending uploads'}</div>
                    </div>
                </div>
            )}

            {pendingQuery.isSuccess && count === 0 && (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">
                        Nothing to review. New chauffeur uploads will appear here.
                    </p>
                </div>
            )}

            {pendingQuery.isSuccess && count > 0 && (
                <ul className="divide-y divide-paper-border-soft">
                    {pending.map((doc) => (
                        <PendingDocRow
                            key={doc.id}
                            doc={doc}
                            onApprove={() => approveMutation.mutate(doc.id)}
                            onReject={() => setRejectingDoc(doc)}
                            isWorking={
                                (approveMutation.isPending && approveMutation.variables === doc.id)
                                || (rejectMutation.isPending && rejectingDoc?.id === doc.id)
                            }
                        />
                    ))}
                </ul>
            )}

            {rejectingDoc && (
                <RejectModal
                    doc={rejectingDoc}
                    onCancel={() => setRejectingDoc(null)}
                    onConfirm={({ reason, notes }) =>
                        rejectMutation.mutate({ docId: rejectingDoc.id, reason, notes })
                    }
                    isSubmitting={rejectMutation.isPending}
                />
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// PendingDocRow — single row in the queue
// -----------------------------------------------------------------------------
// Renders owner context (chauffeur name OR vehicle reg+make), doc type +
// custom label if 'other', file size, expiry (with red text if already
// expired), uploaded-at relative time, View/Approve/Reject buttons.
//
// View opens the file in a new tab via /documents/:id/download-url. Same
// signed-URL pattern dispatch's existing doc surfaces use.
// -----------------------------------------------------------------------------
function PendingDocRow({ doc, onApprove, onReject, isWorking }) {
    const api = useApi();
    const [opening, setOpening] = useState(false);

    // Owner label: driver-owned → chauffeur name; vehicle-owned → reg + make
    // + (paired with X) for fleet partner cars.
    let ownerLabel;
    if (doc.owner_type === 'driver') {
        const name = [doc.driver_first_name, doc.driver_last_name].filter(Boolean).join(' ');
        ownerLabel = name || 'Chauffeur';
    } else {
        const veh = [doc.vehicle_registration, [doc.vehicle_make, doc.vehicle_model].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' · ');
        const partner = doc.vehicle_partner_first_name
            ? ` (paired with ${doc.vehicle_partner_first_name} ${doc.vehicle_partner_last_name || ''})`.trimEnd()
            : '';
        ownerLabel = (veh || 'Vehicle') + partner;
    }

    const docTypeLabel = formatDocTypeLabel(doc.doc_type, doc.custom_label);
    const expiresLabel = doc.expires_at ? formatExpiry(doc.expires_at) : null;
    const uploadedLabel = formatRelativeTime(doc.uploaded_at);

    async function handleView() {
        if (opening) return;
        setOpening(true);
        try {
            const result = await api.get(`/documents/${doc.id}/download-url`);
            const a = window.document.createElement('a');
            a.href = result.download_url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
        } catch (e) {
            toast.error(e.message || 'Could not open document');
        } finally {
            setOpening(false);
        }
    }

    return (
        <li className="px-6 py-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">
                        {docTypeLabel}
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        {doc.owner_type === 'driver' ? 'Chauffeur: ' : 'Vehicle: '}
                        <span className="text-ink-muted">{ownerLabel}</span>
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{doc.file_name}</span>
                        <span className="text-ink-faint">·</span>
                        <span>{(doc.file_size / 1024 / 1024).toFixed(1)} MB</span>
                        <span className="text-ink-faint">·</span>
                        <span>Uploaded {uploadedLabel}</span>
                        {expiresLabel && (
                            <>
                                <span className="text-ink-faint">·</span>
                                <span className={expiresLabel.expired ? 'text-status-danger font-medium' : ''}>
                                    {expiresLabel.text}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                        variant="secondary"
                        onClick={handleView}
                        disabled={opening || isWorking}
                    >
                        {opening ? 'Opening…' : 'View'}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onReject}
                        disabled={isWorking}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onApprove}
                        loading={isWorking}
                        disabled={isWorking}
                    >
                        Approve
                    </Button>
                </div>
            </div>
        </li>
    );
}

// -----------------------------------------------------------------------------
// RejectModal — collect rejection reason + optional notes
// -----------------------------------------------------------------------------
// Reasons match the backend's VALID_REJECTION_REASONS list. Notes are
// required when "other" is picked, optional otherwise. Backend stores as
// 'other:<notes>' for the other case.
// -----------------------------------------------------------------------------
function RejectModal({ doc, onCancel, onConfirm, isSubmitting }) {
    const [reason, setReason] = useState('wrong_document');
    const [notes, setNotes] = useState('');
    const requiresNotes = reason === 'other';

    function handleConfirm() {
        if (requiresNotes && !notes.trim()) {
            toast.error('Please describe the reason');
            return;
        }
        onConfirm({ reason, notes: notes.trim() });
    }

    const docTypeLabel = formatDocTypeLabel(doc.doc_type, doc.custom_label);

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper-card rounded-lg max-w-md w-full p-5 shadow-xl">
                <h3 className="text-sm font-semibold text-ink mb-1">Reject document</h3>
                <p className="text-xs text-ink-subtle mb-4">
                    {docTypeLabel} — {doc.file_name}
                </p>

                <div className="space-y-3">
                    <Select
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <option value="wrong_document">Wrong document uploaded</option>
                        <option value="expired">Document has already expired</option>
                        <option value="unclear_image">Image is unclear or unreadable</option>
                        <option value="details_mismatch">Details don't match</option>
                        <option value="other">Other</option>
                    </Select>

                    {requiresNotes && (
                        <Input
                            label="Details"
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Brief explanation for the chauffeur"
                            disabled={isSubmitting}
                            maxLength={200}
                        />
                    )}

                    <p className="text-xs text-ink-subtle">
                        The chauffeur will see this reason in their portal and can upload a replacement.
                    </p>
                </div>

                <div className="flex gap-2 mt-5 justify-end">
                    <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirm} loading={isSubmitting}>
                        Confirm rejection
                    </Button>
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatDocTypeLabel(docType, customLabel) {
    if (docType === 'other' && customLabel) return customLabel;
    const map = {
        driving_licence: 'Driving licence',
        licence_paper:   'Licence counterpart',
        phv_badge:       'PHV badge',
        profile_picture: 'Profile photo',
        phd_licence:     'PHD licence',
        phd_badge:       'PHD badge',
        dbs_certificate: 'DBS certificate',
        operator_licence:'Operator licence',
        mot:             'MOT certificate',
        insurance:       'Insurance',
        v5:              'V5 logbook',
        phv_licence:     'PHV licence',
        photo_front_exterior: 'Front exterior photo',
        photo_side_exterior:  'Side exterior photo',
        photo_rear_exterior:  'Rear exterior photo',
        photo_front_interior: 'Front interior photo',
        photo_rear_interior:  'Rear interior photo',
        other:           'Other document',
    };
    return map[docType] || docType;
}

function formatExpiry(expiresAt) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiresAt);
    expiry.setHours(0, 0, 0, 0);
    const formatted = expiry.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
    if (expiry < today) {
        return { text: `Expired ${formatted}`, expired: true };
    }
    return { text: `Expires ${formatted}`, expired: false };
}

function formatRelativeTime(timestamp) {
    if (!timestamp) return '';
    const ms = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}

// =============================================================================
// ChauffeurWeeklyReport — full wizard: filters → preview → generate
// =============================================================================
function ChauffeurWeeklyReport() {
    const api = useApi();
    const { getToken } = useAuth();

    // Step 1 — filters
    const defaults = computeLastCompletedWeek();
    const [authority, setAuthority] = useState('TFL');
    const [startDate, setStartDate] = useState(defaults.start);
    const [endDate, setEndDate] = useState(defaults.end);

    // Step 2 — preview list with selection state
    const [selected, setSelected] = useState(() => new Set());
    const [recipientEmail, setRecipientEmail] = useState('');
    const [showEmailField, setShowEmailField] = useState(false);

    // Preview query — disabled until user explicitly hits "Preview". This
    // prevents the page making a fetch on first render and re-fetching on
    // every keystroke in the date inputs.
    const previewQuery = useQuery({
        queryKey: ['compliance', 'chauffeur-preview', authority, startDate, endDate],
        queryFn: () => api.get('/compliance/chauffeur-report/preview', {
            query: { authority, start_date: startDate, end_date: endDate },
        }),
        enabled: false, // manual fetch only
    });

    function handlePreview() {
        // Reset prior selection when filters change between previews
        setSelected(new Set());
        setShowEmailField(false);
        setRecipientEmail('');
        previewQuery.refetch();
    }

    // When new preview data arrives, default-select all rows.
    // Owner can untick the ones they don't want before generating.
    const previewData = previewQuery.data?.chauffeurs;
    useMemo(() => {
        if (previewData && previewData.length > 0) {
            setSelected(new Set(previewData.map((c) => c.id)));
        }
    }, [previewData]);

    function toggle(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function toggleAll() {
        if (!previewData) return;
        if (selected.size === previewData.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(previewData.map((c) => c.id)));
        }
    }

    // CSV download — uses fetch directly because api.get returns parsed JSON.
    // We need the raw blob to trigger a browser download.
    const downloadMutation = useMutation({
        mutationFn: async () => {
            const token = await getToken();
            const res = await fetch('/api/compliance/chauffeur-report/csv', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    authority,
                    start_date: startDate,
                    end_date: endDate,
                    chauffeur_ids: Array.from(selected),
                }),
            });
            if (!res.ok) {
                let detail = '';
                try {
                    const body = await res.json();
                    detail = body?.error || body?.message || '';
                } catch (_) { /* non-JSON */ }
                throw new Error(detail || `CSV generation failed (${res.status})`);
            }
            const blob = await res.blob();
            const dispo = res.headers.get('content-disposition') || '';
            const m = dispo.match(/filename="?([^";]+)"?/i);
            const filename = (m && m[1]) || `Lanz CTS-Driver-${startDate}-${endDate}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = filename;
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            return { filename };
        },
        onSuccess: ({ filename }) => {
            toast.success(`Downloaded ${filename}`);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not generate CSV');
        },
    });

    // Email — sends the same CSV to a recipient via Resend
    const emailMutation = useMutation({
        mutationFn: () => api.post('/compliance/chauffeur-report/email', {
            authority,
            start_date: startDate,
            end_date: endDate,
            chauffeur_ids: Array.from(selected),
            recipient_email: recipientEmail.trim(),
        }),
        onSuccess: (data) => {
            toast.success(`Report sent to ${data.delivered_to}`);
            setShowEmailField(false);
            setRecipientEmail('');
        },
        onError: (err) => {
            toast.error(err.message || 'Could not send email');
        },
    });

    function handleEmailSubmit() {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(recipientEmail.trim())) {
            toast.error('Enter a valid email address');
            return;
        }
        emailMutation.mutate();
    }

    const hasPreview = previewQuery.isSuccess && Array.isArray(previewData);
    const isWorking = downloadMutation.isPending || emailMutation.isPending;
    const allSelected = previewData && selected.size === previewData.length;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Weekly chauffeur report</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    One row per chauffeur allocated to bookings under the selected authority in the date range. Includes completed, no-show, and cancelled bookings.
                </p>
            </div>

            {/* Step 1 — filters */}
            <div className="card-body border-b border-paper-border-soft">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select
                        label="Authority"
                        value={authority}
                        onChange={(e) => setAuthority(e.target.value)}
                        disabled={isWorking}
                    >
                        {AUTHORITIES.map((a) => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </Select>
                    <Input
                        label="From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isWorking}
                    />
                    <Input
                        label="To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isWorking}
                    />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-ink-subtle inline-flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" />
                        Defaults to last completed week (Mon–Sun)
                    </p>
                    <Button
                        variant="primary"
                        onClick={handlePreview}
                        loading={previewQuery.isFetching}
                        disabled={!startDate || !endDate || isWorking}
                    >
                        Preview
                    </Button>
                </div>
            </div>

            {/* Step 2 — preview list */}
            {previewQuery.isError && (
                <div className="card-body border-b border-paper-border-soft">
                    <div className="flex items-start gap-3 text-sm text-status-danger">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                            {previewQuery.error?.message || 'Could not load preview'}
                        </div>
                    </div>
                </div>
            )}

            {hasPreview && previewData.length === 0 && (
                <div className="card-body border-b border-paper-border-soft">
                    <p className="text-sm text-ink-subtle text-center py-4">
                        No chauffeurs were allocated to {authority} bookings in this date range. Try widening the dates or check the authority filter.
                    </p>
                </div>
            )}

            {hasPreview && previewData.length > 0 && (
                <>
                    <div className="card-body border-b border-paper-border-soft">
                        <div className="flex items-center justify-between mb-3">
                            <button
                                type="button"
                                onClick={toggleAll}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
                                disabled={isWorking}
                            >
                                {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                                {allSelected ? 'Untick all' : 'Tick all'}
                            </button>
                            <span className="text-xs text-ink-subtle">
                                {selected.size} of {previewData.length} selected
                            </span>
                        </div>
                        <ul className="divide-y divide-paper-border-soft -mx-6">
                            {previewData.map((c) => {
                                const isOn = selected.has(c.id);
                                return (
                                    <li key={c.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(c.id)}
                                            disabled={isWorking}
                                            className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-paper-hover/50 transition-colors disabled:opacity-50"
                                        >
                                            {isOn
                                                ? <CheckSquare className="h-4 w-4 text-ink flex-shrink-0" />
                                                : <Square className="h-4 w-4 text-ink-faint flex-shrink-0" />
                                            }
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-ink">
                                                    {c.first_name} {c.last_name}
                                                    {c.call_sign && (
                                                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-ink text-paper font-mono text-2xs font-medium">
                                                            {c.call_sign}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-ink-subtle mt-0.5">
                                                    Licence {c.phv_licence_number || '—'} · {c.booking_count} {c.booking_count === 1 ? 'booking' : 'bookings'} this period
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Step 3 — generate */}
                    <div className="card-body">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEmailField((v) => !v)}
                                disabled={selected.size === 0 || isWorking}
                            >
                                <Mail className="h-4 w-4" />
                                Email CSV
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => downloadMutation.mutate()}
                                loading={downloadMutation.isPending}
                                disabled={selected.size === 0 || isWorking}
                            >
                                <Download className="h-4 w-4" />
                                Download CSV
                            </Button>
                        </div>

                        {showEmailField && (
                            <div className="mt-4 pt-4 border-t border-paper-border-soft">
                                <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex-1 min-w-[240px]">
                                        <Input
                                            label="Recipient email"
                                            type="email"
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder="compliance@authority.gov.uk"
                                            disabled={emailMutation.isPending}
                                            hint="Sent from reservations@lanzcts.com with the CSV attached"
                                        />
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleEmailSubmit}
                                        loading={emailMutation.isPending}
                                        disabled={!recipientEmail.trim() || selected.size === 0}
                                    >
                                        {emailMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                        Send
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

            // =============================================================================
// VehicleWeeklyReport — same wizard pattern as chauffeur, swapped for vehicle
// data. Rather than abstract the two into one shared component, keeping them
// as siblings means each can evolve independently if TFL ever asks for
// vehicle-specific columns or different filter behaviour.
// =============================================================================
function VehicleWeeklyReport() {
    const api = useApi();
    const { getToken } = useAuth();

    const defaults = computeLastCompletedWeek();
    const [authority, setAuthority] = useState('TFL');
    const [startDate, setStartDate] = useState(defaults.start);
    const [endDate, setEndDate] = useState(defaults.end);

    const [selected, setSelected] = useState(() => new Set());
    const [recipientEmail, setRecipientEmail] = useState('');
    const [showEmailField, setShowEmailField] = useState(false);

    const previewQuery = useQuery({
        queryKey: ['compliance', 'vehicle-preview', authority, startDate, endDate],
        queryFn: () => api.get('/compliance/vehicle-report/preview', {
            query: { authority, start_date: startDate, end_date: endDate },
        }),
        enabled: false,
    });

    function handlePreview() {
        setSelected(new Set());
        setShowEmailField(false);
        setRecipientEmail('');
        previewQuery.refetch();
    }

    const previewData = previewQuery.data?.vehicles;
    useMemo(() => {
        if (previewData && previewData.length > 0) {
            setSelected(new Set(previewData.map((v) => v.id)));
        }
    }, [previewData]);

    function toggle(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function toggleAll() {
        if (!previewData) return;
        if (selected.size === previewData.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(previewData.map((v) => v.id)));
        }
    }

    const downloadMutation = useMutation({
        mutationFn: async () => {
            const token = await getToken();
            const res = await fetch('/api/compliance/vehicle-report/csv', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    authority,
                    start_date: startDate,
                    end_date: endDate,
                    vehicle_ids: Array.from(selected),
                }),
            });
            if (!res.ok) {
                let detail = '';
                try {
                    const body = await res.json();
                    detail = body?.error || body?.message || '';
                } catch (_) { /* non-JSON */ }
                throw new Error(detail || `CSV generation failed (${res.status})`);
            }
            const blob = await res.blob();
            const dispo = res.headers.get('content-disposition') || '';
            const m = dispo.match(/filename="?([^";]+)"?/i);
            const filename = (m && m[1]) || `Lanz CTS-Vehicle-${startDate}-${endDate}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = filename;
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            return { filename };
        },
        onSuccess: ({ filename }) => {
            toast.success(`Downloaded ${filename}`);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not generate CSV');
        },
    });

    const emailMutation = useMutation({
        mutationFn: () => api.post('/compliance/vehicle-report/email', {
            authority,
            start_date: startDate,
            end_date: endDate,
            vehicle_ids: Array.from(selected),
            recipient_email: recipientEmail.trim(),
        }),
        onSuccess: (data) => {
            toast.success(`Report sent to ${data.delivered_to}`);
            setShowEmailField(false);
            setRecipientEmail('');
        },
        onError: (err) => {
            toast.error(err.message || 'Could not send email');
        },
    });

    function handleEmailSubmit() {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(recipientEmail.trim())) {
            toast.error('Enter a valid email address');
            return;
        }
        emailMutation.mutate();
    }

    const hasPreview = previewQuery.isSuccess && Array.isArray(previewData);
    const isWorking = downloadMutation.isPending || emailMutation.isPending;
    const allSelected = previewData && selected.size === previewData.length;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Weekly vehicle report</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    One row per vehicle allocated to bookings under the selected authority in the date range. Includes completed, no-show, and cancelled bookings.
                </p>
            </div>

            {/* Filters */}
            <div className="card-body border-b border-paper-border-soft">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select
                        label="Authority"
                        value={authority}
                        onChange={(e) => setAuthority(e.target.value)}
                        disabled={isWorking}
                    >
                        {AUTHORITIES.map((a) => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </Select>
                    <Input
                        label="From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isWorking}
                    />
                    <Input
                        label="To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isWorking}
                    />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-ink-subtle inline-flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" />
                        Defaults to last completed week (Mon–Sun)
                    </p>
                    <Button
                        variant="primary"
                        onClick={handlePreview}
                        loading={previewQuery.isFetching}
                        disabled={!startDate || !endDate || isWorking}
                    >
                        Preview
                    </Button>
                </div>
            </div>

            {/* Errors / empty */}
            {previewQuery.isError && (
                <div className="card-body border-b border-paper-border-soft">
                    <div className="flex items-start gap-3 text-sm text-status-danger">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                            {previewQuery.error?.message || 'Could not load preview'}
                        </div>
                    </div>
                </div>
            )}

            {hasPreview && previewData.length === 0 && (
                <div className="card-body border-b border-paper-border-soft">
                    <p className="text-sm text-ink-subtle text-center py-4">
                        No vehicles were allocated to {authority} bookings in this date range. Try widening the dates or check the authority filter.
                    </p>
                </div>
            )}

            {hasPreview && previewData.length > 0 && (
                <>
                    <div className="card-body border-b border-paper-border-soft">
                        <div className="flex items-center justify-between mb-3">
                            <button
                                type="button"
                                onClick={toggleAll}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
                                disabled={isWorking}
                            >
                                {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                                {allSelected ? 'Untick all' : 'Tick all'}
                            </button>
                            <span className="text-xs text-ink-subtle">
                                {selected.size} of {previewData.length} selected
                            </span>
                        </div>
                        <ul className="divide-y divide-paper-border-soft -mx-6">
                            {previewData.map((v) => {
                                const isOn = selected.has(v.id);
                                return (
                                    <li key={v.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(v.id)}
                                            disabled={isWorking}
                                            className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-paper-hover/50 transition-colors disabled:opacity-50"
                                        >
                                            {isOn
                                                ? <CheckSquare className="h-4 w-4 text-ink flex-shrink-0" />
                                                : <Square className="h-4 w-4 text-ink-faint flex-shrink-0" />
                                            }
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-ink">
                                                    <span className="font-mono">{v.registration}</span>
                                                    <span className="ml-2 text-ink-muted font-normal">
                                                        {v.make} {v.model}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-ink-subtle mt-0.5">
                                                    Licence {v.licence_plate_number || '—'} · {v.booking_count} {v.booking_count === 1 ? 'booking' : 'bookings'} this period
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Generate */}
                    <div className="card-body">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEmailField((v) => !v)}
                                disabled={selected.size === 0 || isWorking}
                            >
                                <Mail className="h-4 w-4" />
                                Email CSV
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => downloadMutation.mutate()}
                                loading={downloadMutation.isPending}
                                disabled={selected.size === 0 || isWorking}
                            >
                                <Download className="h-4 w-4" />
                                Download CSV
                            </Button>
                        </div>

                        {showEmailField && (
                            <div className="mt-4 pt-4 border-t border-paper-border-soft">
                                <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex-1 min-w-[240px]">
                                        <Input
                                            label="Recipient email"
                                            type="email"
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder="compliance@authority.gov.uk"
                                            disabled={emailMutation.isPending}
                                            hint="Sent from reservations@lanzcts.com with the CSV attached"
                                        />
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleEmailSubmit}
                                        loading={emailMutation.isPending}
                                        disabled={!recipientEmail.trim() || selected.size === 0}
                                    >
                                        {emailMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                        Send
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// =============================================================================
// BookingsReport — quick-lookup audit log over a period (default last 6 months).
//
// Differs from chauffeur/vehicle reports:
//   - No tick step. At 6-month volumes (~hundreds to thousands of rows) a
//     row-by-row UI is theatre, not a real safety net. Authority + date
//     filters define the dataset; trust them.
//   - Preview shows summary stats (count, status breakdown, total value)
//     so owner can sanity-check magnitude before generating.
//   - 8 fixed columns, no column picker.
// =============================================================================
function BookingsReport() {
    const api = useApi();
    const { getToken } = useAuth();

    const defaults = computeLastSixMonths();
    const [authority, setAuthority] = useState('TFL');
    const [startDate, setStartDate] = useState(defaults.start);
    const [endDate, setEndDate] = useState(defaults.end);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [showEmailField, setShowEmailField] = useState(false);

    const previewQuery = useQuery({
        queryKey: ['compliance', 'bookings-preview', authority, startDate, endDate],
        queryFn: () => api.get('/compliance/bookings-report/preview', {
            query: { authority, start_date: startDate, end_date: endDate },
        }),
        enabled: false,
    });

    function handlePreview() {
        setShowEmailField(false);
        setRecipientEmail('');
        previewQuery.refetch();
    }

    const downloadMutation = useMutation({
        mutationFn: async () => {
            const token = await getToken();
            const res = await fetch('/api/compliance/bookings-report/csv', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    authority,
                    start_date: startDate,
                    end_date: endDate,
                }),
            });
            if (!res.ok) {
                let detail = '';
                try {
                    const body = await res.json();
                    detail = body?.error || body?.message || '';
                } catch (_) { /* non-JSON */ }
                throw new Error(detail || `CSV generation failed (${res.status})`);
            }
            const blob = await res.blob();
            const dispo = res.headers.get('content-disposition') || '';
            const m = dispo.match(/filename="?([^";]+)"?/i);
            const filename = (m && m[1]) || `Lanz CTS-Bookings-${startDate}-${endDate}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = filename;
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            return { filename };
        },
        onSuccess: ({ filename }) => {
            toast.success(`Downloaded ${filename}`);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not generate CSV');
        },
    });

    const emailMutation = useMutation({
        mutationFn: () => api.post('/compliance/bookings-report/email', {
            authority,
            start_date: startDate,
            end_date: endDate,
            recipient_email: recipientEmail.trim(),
        }),
        onSuccess: (data) => {
            toast.success(`Report sent to ${data.delivered_to}`);
            setShowEmailField(false);
            setRecipientEmail('');
        },
        onError: (err) => {
            toast.error(err.message || 'Could not send email');
        },
    });

    function handleEmailSubmit() {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(recipientEmail.trim())) {
            toast.error('Enter a valid email address');
            return;
        }
        emailMutation.mutate();
    }

    const summary = previewQuery.data?.summary;
    const hasResults = summary && summary.total > 0;
    const isWorking = downloadMutation.isPending || emailMutation.isPending;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Bookings report</h2>
                <p className="text-xs text-ink-subtle mt-0.5">
                    Quick-lookup audit log over a period. Includes completed, no-show, and cancelled bookings.
                </p>
            </div>

            {/* Filters */}
            <div className="card-body border-b border-paper-border-soft">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select
                        label="Authority"
                        value={authority}
                        onChange={(e) => setAuthority(e.target.value)}
                        disabled={isWorking}
                    >
                        {AUTHORITIES.map((a) => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </Select>
                    <Input
                        label="From"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isWorking}
                    />
                    <Input
                        label="To"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isWorking}
                    />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-ink-subtle inline-flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" />
                        Defaults to the last 6 months
                    </p>
                    <Button
                        variant="primary"
                        onClick={handlePreview}
                        loading={previewQuery.isFetching}
                        disabled={!startDate || !endDate || isWorking}
                    >
                        Preview
                    </Button>
                </div>
            </div>

            {previewQuery.isError && (
                <div className="card-body border-b border-paper-border-soft">
                    <div className="flex items-start gap-3 text-sm text-status-danger">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>{previewQuery.error?.message || 'Could not load preview'}</div>
                    </div>
                </div>
            )}

            {previewQuery.isSuccess && summary?.total === 0 && (
                <div className="card-body border-b border-paper-border-soft">
                    <p className="text-sm text-ink-subtle text-center py-4">
                        No bookings match the supplied filters.
                    </p>
                </div>
            )}

            {hasResults && (
                <>
                    {/* Summary stats */}
                    <div className="card-body border-b border-paper-border-soft">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <SummaryTile label="Total bookings" value={summary.total.toLocaleString()} />
                            <SummaryTile label="Completed" value={summary.completed.toLocaleString()} />
                            <SummaryTile label="No-show" value={summary.no_show.toLocaleString()} />
                            <SummaryTile label="Cancelled" value={summary.cancelled.toLocaleString()} />
                        </div>
                        <div className="mt-3 text-xs text-ink-subtle text-right">
                            Total value: £{Number(summary.total_value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>

                    {/* Generate */}
                    <div className="card-body">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEmailField((v) => !v)}
                                disabled={isWorking}
                            >
                                <Mail className="h-4 w-4" />
                                Email CSV
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => downloadMutation.mutate()}
                                loading={downloadMutation.isPending}
                                disabled={isWorking}
                            >
                                <Download className="h-4 w-4" />
                                Download CSV
                            </Button>
                        </div>

                        {showEmailField && (
                            <div className="mt-4 pt-4 border-t border-paper-border-soft">
                                <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex-1 min-w-[240px]">
                                        <Input
                                            label="Recipient email"
                                            type="email"
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder="compliance@authority.gov.uk"
                                            disabled={emailMutation.isPending}
                                            hint="Sent from reservations@lanzcts.com with the CSV attached"
                                        />
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleEmailSubmit}
                                        loading={emailMutation.isPending}
                                        disabled={!recipientEmail.trim()}
                                    >
                                        {emailMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                        Send
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function SummaryTile({ label, value }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider font-medium text-ink-subtle">
                {label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-ink">
                {value}
            </div>
        </div>
    );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Compute the last fully-completed week (Mon–Sun) ending before today.
 * Returns YYYY-MM-DD strings ready to slot into the date inputs.
 *
 * Examples (assuming today is the date in the comment):
 *   today = Sun 03 May → Mon 20 Apr to Sun 26 Apr
 *   today = Mon 04 May → Mon 20 Apr to Sun 26 Apr (still last week, not last 7d)
 *   today = Wed 06 May → Mon 27 Apr to Sun 03 May
 */
function computeLastCompletedWeek() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Day-of-week with Mon=0..Sun=6 (JS native is Sun=0..Sat=6)
    const jsDay = today.getDay();
    const monIndex = (jsDay + 6) % 7;

    // Sunday of the last completed week = today - monIndex - 1
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - monIndex - 1);

    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);

    return {
        start: toISO(lastMonday),
        end: toISO(lastSunday),
    };
}

function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Compute the last 6 months ending today. Returns YYYY-MM-DD strings ready
 * for the date inputs.
 */
function computeLastSixMonths() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setMonth(start.getMonth() - 6);
    return {
        start: toISO(start),
        end: toISO(today),
    };
}
